import * as React from "react";

import * as Redux from "react-redux";

import { useLocalStorageState } from "client/hooks";
import {
  UIFieldMap,
  useFeatureFlags,
  useMerchantModel,
  useSaveFormCompletion,
  useUiFieldMap,
} from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { MerchantFieldMapper } from "client/lib/apt-merchant";
import { UserService } from "client/services";
import { RootState } from "client/store";
import { errorActions } from "client/store";

import { FormBuilder } from "../FormBuilder";

import { BusinessAddressConfirmationForm } from "./BusinessAddressConfirmationForm";
import { BusinessAddressHelperText } from "./BusinessAddressHelperText";
import { CraRegistryIdHelperText } from "./CraRegistryIdHelperText";
import { StreetAddressHelperText } from "./StreetAddressHelperText";

export type CompanyInfoFormFieldUiMap = MerchantFieldMapper<UIFieldMap>;

// field order reflects input order in UI
export const companyInfoUiFieldMap: CompanyInfoFormFieldUiMap = {
  companyInformation: {
    registeredCountry: { width: 12 },
    businessType: { width: 12 },
    companyNumber: { width: 12 },
    registrationNumber: { width: 12 },
    trustDeed: { width: 12 }, // file upload
    legalBusinessName: { width: 12 },
    stockMarketLister: { width: 12 },
    hasConstitution: { width: 12 },
    hasShareholderAgreement: { width: 12 },
    craBusinessNumber: { width: 12 },
    craRegistryId: { width: 12, customHelperText: <CraRegistryIdHelperText /> },
    ein: { width: 12 },
    abn: { width: 12 },
    businessStreet: {
      width: 12,
      customHelperText: <StreetAddressHelperText />,
      customFieldHeader: <BusinessAddressHelperText />,
    },
    businessStreet2: { width: 12 },
    businessCity: {},
    businessState: {},
    businessPostalCode: {},
    partnershipAgreement: { width: 12 }, // file upload
    proofOfInsurance: { width: 12 }, // file upload
  },
};

export const CompanyInformationForm: React.FC = () => {
  const {
    formatMerchantForSave,
    formatMerchantForStoreInfo,
    merchant,
    setOnboardingLink,
  } = useMerchantModel();

  const ONBOARDING_LINK_DELAY = 8000;

  const [
    isBusinessAddressConfirmed,
    setIsBusinessAddressConfirmed,
  ] = useLocalStorageState("isBusinessAddressConfirmed", false);

  const merchantAccount = Redux.useSelector(
    ({ merchant: { merchantAccount } }: RootState) => merchantAccount
  );

  const dispatch = Redux.useDispatch();

  const { mutate: completeCompanyInfo } = useSaveFormCompletion(dispatch);

  const { orderedUiFieldMap } = useUiFieldMap(companyInfoUiFieldMap);

  const [
    { merchantPortalOperationalGenerateOnboardingLink },
  ] = useFeatureFlags();

  const genOnboardingLink = (prospectId: string) => {
    UserService.generateOnboardingLink(prospectId).then(({ onboardingLink }) =>
      setOnboardingLink(onboardingLink)
    );
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // throw an error if user tries to submit company information form without
    // checking the box to confirm their business address matches the one in their NZBN
    if (
      !isBusinessAddressConfirmed &&
      merchant.companyInformation.registeredCountry.value === "New Zealand"
    ) {
      dispatch(
        errorActions.setError(
          "Please confirm this address information matches the one registered to your NZBN before submitting"
        )
      );
      return;
    }

    completeCompanyInfo({
      saveData: formatMerchantForSave(),
      formCompletionData: {
        companyInfoForm: true,
      },
      storeData: formatMerchantForStoreInfo(),
    });

    // Generating the onboarding link on submit because when the
    // Lead form is submitted or the CompanyInformationForm loads,
    // the Prospect object in SF is not initialized to query and
    // the link generation fails
    // Also adding a non-blocking delay of 8 seconds to avoid the race condition
    if (
      merchantAccount.mlsProspectRef &&
      !merchantAccount.onboardingLink &&
      merchantPortalOperationalGenerateOnboardingLink
    ) {
      setTimeout(
        genOnboardingLink,
        ONBOARDING_LINK_DELAY,
        merchantAccount.mlsProspectRef
      );
    }
  };

  return (
    <FormBuilder
      fieldSet={merchant.companyInformation}
      onSubmit={handleSubmit}
      uiFieldMap={orderedUiFieldMap.companyInformation}
      footer={
        <BusinessAddressConfirmationForm
          isBusinessAddressConfirmed={isBusinessAddressConfirmed}
          onChange={(_, checked) => {
            setIsBusinessAddressConfirmed(checked);
            if (checked) {
              logAmplitudeEvent(
                "REDESIGN_ADD_YOUR_STORE",
                "checked_confirm_business_address_checkbox"
              );
            }
          }}
        />
      }
    />
  );
};
